// src/components/StaffVaccineReservationList.js
import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Spinner,
  Text,
  FormControl,
  FormLabel,
  Input,
  Select,
  Flex,
  Grid,
  GridItem,
  Tooltip,
  IconButton as ChakraIconButton,
  useBreakpointValue,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileLines, faSearch, faCircle } from "@fortawesome/free-solid-svg-icons";
import DataTable from "./AppDataTable";
import { tableCustomStyles } from "../themes/TableStyles";
import apiService from "../services/ApiService";
import { useAuth } from "../context/AuthContext";
import * as IconButton from "../themes/IconButtonSet";
import { Search, Clear } from "../themes/IconButtonSet";
import { StandardHeader } from "../themes/StandardHeader";
import { convertToThDate } from "./CustomDatePicker";
import {
  getValidPeriods,
  getEndPeriodOptions,
  isPeriodAfter,
} from "../utils/deliveryPeriod";

const statusConfig = {
  ใบจองใหม่: { color: "blue.500", label: "ใบจองใหม่" },
  รอการอนุมัติ: { color: "orange.500", label: "รอการอนุมัติ" },
  ยืนยันการจอง: { color: "green.500", label: "ยืนยันการจอง" },
  พร้อมรับวัคซีน: { color: "pink.500", label: "พร้อมรับวัคซีน" },
  รับวัคซีนแล้ว: { color: "purple.500", label: "รับวัคซีนแล้ว" },
  ยกเลิกการจอง: { color: "red.500", label: "ยกเลิกการจอง" },
  ปฏิเสธการจอง: { color: "red.600", label: "ปฏิเสธการจอง" },
  เกินกำหนดรับ: { color: "gray.500", label: "เกินกำหนดรับ" },
};

const StatusBadgeWithDot = ({ status }) => {
  const config = statusConfig[status] || { color: "gray.500" };

  return (
    <HStack spacing={2} align="center" minW={0}>
      <Box color={config.color} display="flex" alignItems="center">
        <FontAwesomeIcon icon={faCircle} size="xs" />
      </Box>
      <Text fontSize={{ base: "sm", xl: "md" }} noOfLines={1}>
        {status || "-"}
      </Text>
    </HStack>
  );
};

// ต่ำกว่า lg ตารางมีคอลัมน์มากเกินกว่าจะอ่านได้ จึงสลับไปแสดงเป็นการ์ดแทน
const MobileReservationList = ({ data, pickupPoints, onOpenDetail }) => {
  if (data.length === 0) {
    return (
      <Text fontSize="lg" color="gray.500" textAlign="center" w="100%">
        ไม่พบข้อมูลใบจองวัคซีนตามเงื่อนไขที่ค้นหา
      </Text>
    );
  }

  return (
    <VStack spacing={4} w="100%">
      {data.map((row) => {
        const point = pickupPoints.find(
          (p) => p.WarehouseID === row.PickupPoint
        );
        const pickupPointName = point
          ? point.WarehouseName
          : row.PickupPoint || "-";

        return (
          <Box
            key={row.ReservationID}
            w="100%"
            p={{ base: 3, sm: 4 }}
            borderWidth={1}
            borderRadius="lg"
            boxShadow="sm"
          >
            <VStack align="start" spacing={3}>
              <Flex
                w="100%"
                justify="space-between"
                align="flex-start"
                gap={2}
                minW={0}
              >
                <VStack align="start" spacing={1} minW={0} flex="1">
                  <Text fontWeight="bold" fontSize="md" noOfLines={1}>
                    {row.ReservationID}
                  </Text>
                  <StatusBadgeWithDot status={row.ReservationStatus} />
                </VStack>
                <Tooltip label="ดูรายละเอียด" placement="top" hasArrow>
                  <ChakraIconButton
                    icon={<FontAwesomeIcon icon={faFileLines} />}
                    size="sm"
                    variant="ghost"
                    colorScheme="blue"
                    aria-label="ดูรายละเอียด"
                    flexShrink={0}
                    onClick={() => onOpenDetail(row.ReservationID)}
                  />
                </Tooltip>
              </Flex>

              <Box w="100%" pt={2} borderTopWidth="1px" borderColor="gray.200">
                <Grid
                  templateColumns="auto 1fr"
                  gapX={2}
                  gapY={1}
                  fontSize="sm"
                  color="gray.600"
                >
                  <GridItem fontWeight="medium">ชื่อเกษตรกร:</GridItem>
                  <GridItem>{row.FarmerName || "-"}</GridItem>

                  <GridItem fontWeight="medium">รหัสเกษตรกร:</GridItem>
                  <GridItem>{row.FarmerID || "-"}</GridItem>

                  <GridItem fontWeight="medium">เบอร์โทร:</GridItem>
                  <GridItem>{row.FarmerTel || "-"}</GridItem>

                  <GridItem fontWeight="medium">รอบจัดส่ง:</GridItem>
                  <GridItem>{row.DeliveryPeriod || "-"}</GridItem>

                  <GridItem fontWeight="medium">จุดจำหน่าย:</GridItem>
                  <GridItem>{pickupPointName}</GridItem>

                  <GridItem fontWeight="medium">วันที่จอง:</GridItem>
                  <GridItem>{convertToThDate(row.ReservationDate)}</GridItem>
                </Grid>
              </Box>
            </VStack>
          </Box>
        );
      })}
    </VStack>
  );
};

const statusOptions = [
  "ใบจองใหม่",
  "รอการอนุมัติ",
  "ยืนยันการจอง",
  "พร้อมรับวัคซีน",
  "รับวัคซีนแล้ว",
  "ยกเลิกการจอง",
];

const getDefaultFiltersFromPeriods = (periods) => {
  const validPeriods = getValidPeriods(periods);
  const recentPeriods = validPeriods.slice(0, 2);
  const endPeriod = recentPeriods[0]?.DeliveryPeriod || "";
  const startPeriod =
    recentPeriods.length >= 2
      ? recentPeriods[1].DeliveryPeriod
      : endPeriod;

  return {
    StartDeliveryPeriod: startPeriod,
    EndDeliveryPeriod: endPeriod,
    PickupPoint: "",
    ReservationStatus: "",
    ReservationID: "",
    FarmerName: "",
  };
};

const emptyFilters = {
  StartDeliveryPeriod: "",
  EndDeliveryPeriod: "",
  PickupPoint: "",
  ReservationStatus: "",
  ReservationID: "",
  FarmerName: "",
};

const StaffVaccineReservationList = () => {
  const navigate = useNavigate();
  const { sessionId } = useAuth();

  const [reservations, setReservations] = useState([]);
  const [pickupPoints, setPickupPoints] = useState([]);
  const [deliveryPeriods, setDeliveryPeriods] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filterData, setFilterData] = useState(emptyFilters);

  // ต่ำกว่า lg ตารางจะแน่นเกินไป จึงสลับไปใช้การ์ดแทน
  const isMobile = useBreakpointValue(
    { base: true, lg: false },
    { ssr: false }
  );

  const columns = [
    {
      name: "เลขที่ใบจอง",
      selector: (row) => row.ReservationID,
      sortable: true,
      minWidth: "140px",
      grow: 0,
    },
    {
      name: "ชื่อเกษตรกร",
      selector: (row) => row.FarmerName || "-",
      sortable: true,
      minWidth: "180px",
      wrap: true,
    },
    {
      name: "รหัสเกษตรกร",
      selector: (row) => row.FarmerID || "-",
      sortable: true,
      minWidth: "150px",
      hide: 1600,
    },
    {
      name: "เบอร์โทร",
      selector: (row) => row.FarmerTel || "-",
      sortable: true,
      minWidth: "130px",
      hide: 1400,
    },
    {
      name: "รอบการจัดส่ง",
      selector: (row) => row.DeliveryPeriod || "-",
      sortable: true,
      minWidth: "130px",
      wrap: true,
    },
    {
      name: "จุดจำหน่าย",
      selector: (row) => {
        const point = pickupPoints.find((p) => p.WarehouseID === row.PickupPoint);
        return point ? point.WarehouseName : row.PickupPoint || "-";
      },
      sortable: true,
      minWidth: "170px",
      wrap: true,
    },
    {
      name: "วันที่จอง",
      selector: (row) => convertToThDate(row.ReservationDate),
      sortable: true,
      minWidth: "130px",
      grow: 0,
    },
    {
      name: "สถานะ",
      cell: (row) => <StatusBadgeWithDot status={row.ReservationStatus} />,
      selector: (row) => row.ReservationStatus,
      sortable: true,
      minWidth: "150px",
    },
    {
      name: "การดำเนินการ",
      cell: (row) => (
        <IconButton.View
          icon={faFileLines}
          onClick={() =>
            navigate(`/staffvaccinereservationdetail/${row.ReservationID}`)
          }
          label="รายละเอียด"
          size="sm"
        />
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      minWidth: "140px",
    },
  ];

  const fetchPickupPoints = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccinePickupPointList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setPickupPoints(response.VaccineWarehousesList || []);
      }
    } catch (error) {
      console.error("Error fetching pickup points:", error);
    }
  };

  const fetchDeliveryPeriods = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineReservationPeriodList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        const periods = response.DeliveryPeriods || [];
        const validPeriods = getValidPeriods(periods);
        setDeliveryPeriods(validPeriods);
        return validPeriods;
      }
    } catch (error) {
      console.error("Error fetching delivery periods:", error);
    }
    return [];
  };

  const fetchReservations = async (filters = filterData) => {
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineReservationsList",
        data: {
          StartDeliveryPeriod: filters.StartDeliveryPeriod || "",
          EndDeliveryPeriod: filters.EndDeliveryPeriod || "",
          PickupPoint: filters.PickupPoint || "",
          ReservationStatus: filters.ReservationStatus || "",
          ReservationID: filters.ReservationID?.trim() || "",
          FarmerName: filters.FarmerName?.trim() || "",
        },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setReservations(response.VaccineReservationsList || []);
      } else {
        setReservations([]);
      }
    } catch (error) {
      console.error("Error fetching reservations:", error);
      setReservations([]);
    } finally {
      setIsLoading(false);
    }
  };

  const initPage = async () => {
    setIsLoading(true);
    try {
      const [, periods] = await Promise.all([
        fetchPickupPoints(),
        fetchDeliveryPeriods(),
      ]);
      const defaultFilters = getDefaultFiltersFromPeriods(periods);
      setFilterData(defaultFilters);
      await fetchReservations(defaultFilters);
    } catch (error) {
      console.error("Error initializing page:", error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId) {
      initPage();
    }
  }, [sessionId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilterData((prev) => {
      const updated = { ...prev, [name]: value };
      if (
        name === "StartDeliveryPeriod" &&
        value &&
        prev.EndDeliveryPeriod &&
        isPeriodAfter(deliveryPeriods, value, prev.EndDeliveryPeriod)
      ) {
        updated.EndDeliveryPeriod = value;
      }
      return updated;
    });
  };

  const handleSearch = () => {
    fetchReservations(filterData);
  };

  const handleClearSearch = async () => {
    const periods = deliveryPeriods.length
      ? deliveryPeriods
      : await fetchDeliveryPeriods();
    const resetFilters = getDefaultFiltersFromPeriods(periods);
    setFilterData(resetFilters);
    fetchReservations(resetFilters);
  };

  const handleCreateNew = () => {
    navigate("/staffvaccinereservationdetail/new");
  };

  const openReservationDetail = (reservationID) => {
    navigate(`/staffvaccinereservationdetail/${reservationID}`);
  };

  const endPeriodOptions = getEndPeriodOptions(
    deliveryPeriods,
    filterData.StartDeliveryPeriod
  );

  return (
    <Box
      p={{ base: 3, sm: 4, md: 6 }}
      m={{ base: 2, md: 4 }}
      borderWidth={1}
      borderRadius="lg"
      boxShadow="lg"
    >
      <VStack spacing={{ base: 4, md: 6 }} align="start">
        <StandardHeader
          title="รายการใบจองวัคซีน (สำหรับเจ้าหน้าที่)"
          bgColor="iBlue.600"
          icon={faFileLines}
        />

        <Box
          borderWidth={1}
          borderRadius="lg"
          boxShadow="lg"
          p={{ base: 3, sm: 4, md: 5 }}
          w="100%"
        >
          <StandardHeader
            title="ค้นหาข้อมูลใบจองวัคซีน"
            bgColor="iBlue.600"
            icon={faSearch}
          />
          <VStack spacing={{ base: 4, md: 6 }} p={{ base: 0, md: 5 }} pt={4}>
            <Grid
              w="100%"
              gap={{ base: 3, md: 4 }}
              templateColumns={{
                base: "1fr",
                md: "repeat(2, minmax(0, 1fr))",
                xl: "repeat(3, minmax(0, 1fr))",
              }}
            >
              <FormControl>
                <FormLabel fontSize={{ base: "sm", md: "md" }} color="gray.600" mb={1}>
                  รอบการจัดส่ง (ตั้งแต่)
                </FormLabel>
                <Select
                  name="StartDeliveryPeriod"
                  value={filterData.StartDeliveryPeriod}
                  onChange={handleInputChange}
                  placeholder="เลือกรอบเริ่มต้น"
                  colorScheme="iBlue"
                >
                  {deliveryPeriods.map((period) => (
                    <option
                      key={period.DeliveryPeriod}
                      value={period.DeliveryPeriod}
                    >
                      {period.DeliveryPeriod}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel fontSize={{ base: "sm", md: "md" }} color="gray.600" mb={1}>
                  รอบการจัดส่ง (ถึง)
                </FormLabel>
                <Select
                  name="EndDeliveryPeriod"
                  value={filterData.EndDeliveryPeriod}
                  onChange={handleInputChange}
                  placeholder="เลือกรอบสิ้นสุด"
                  colorScheme="iBlue"
                >
                  {endPeriodOptions.map((period) => (
                    <option
                      key={period.DeliveryPeriod}
                      value={period.DeliveryPeriod}
                    >
                      {period.DeliveryPeriod}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel fontSize={{ base: "sm", md: "md" }} color="gray.600" mb={1}>
                  จุดจำหน่าย
                </FormLabel>
                <Select
                  name="PickupPoint"
                  value={filterData.PickupPoint}
                  onChange={handleInputChange}
                  placeholder="เลือกจุดจำหน่าย (ทั้งหมด)"
                  colorScheme="iBlue"
                >
                  {pickupPoints.map((point) => (
                    <option key={point.WarehouseID} value={point.WarehouseID}>
                      {point.WarehouseName}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel fontSize={{ base: "sm", md: "md" }} color="gray.600" mb={1}>
                  สถานะใบจอง
                </FormLabel>
                <Select
                  name="ReservationStatus"
                  value={filterData.ReservationStatus}
                  onChange={handleInputChange}
                  placeholder="เลือกสถานะ (ทั้งหมด)"
                  colorScheme="iBlue"
                >
                  {statusOptions.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel fontSize={{ base: "sm", md: "md" }} color="gray.600" mb={1}>
                  เลขที่ใบจอง
                </FormLabel>
                <Input
                  name="ReservationID"
                  value={filterData.ReservationID}
                  onChange={handleInputChange}
                  placeholder="ค้นหาเลขที่ใบจอง"
                  colorScheme="iBlue"
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize={{ base: "sm", md: "md" }} color="gray.600" mb={1}>
                  ชื่อเกษตรกร
                </FormLabel>
                <Input
                  name="FarmerName"
                  value={filterData.FarmerName}
                  onChange={handleInputChange}
                  placeholder="ค้นหาชื่อเกษตรกร"
                  colorScheme="iBlue"
                />
              </FormControl>
            </Grid>
            <Flex
              w="100%"
              gap={3}
              direction={{ base: "column", sm: "row" }}
              justify={{ sm: "flex-end" }}
            >
              <Clear
                onClick={handleClearSearch}
                w={{ base: "100%", sm: "auto" }}
              />
              <Search
                onClick={handleSearch}
                isLoading={isLoading}
                w={{ base: "100%", sm: "auto" }}
              />
            </Flex>
          </VStack>
        </Box>

        <Flex w="100%" justify={{ base: "stretch", sm: "flex-end" }}>
          <IconButton.Add
            onClick={handleCreateNew}
            label="สร้างใบจองใหม่"
            w={{ base: "100%", sm: "auto" }}
          />
        </Flex>

        {isLoading ? (
          <Flex justify="center" w="100%">
            <Spinner size="xl" />
          </Flex>
        ) : isMobile ? (
          <MobileReservationList
            data={reservations}
            pickupPoints={pickupPoints}
            onOpenDetail={openReservationDetail}
          />
        ) : reservations.length === 0 ? (
          <Text fontSize="lg" color="gray.500" textAlign="center" w="100%">
            ไม่พบข้อมูลใบจองวัคซีนตามเงื่อนไขที่ค้นหา
          </Text>
        ) : (
          <Box w="100%" overflowX="auto">
            <DataTable
              columns={columns}
              data={reservations}
              pagination
              highlightOnHover
              striped
              responsive
              customStyles={tableCustomStyles}
            />
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default StaffVaccineReservationList;
